import { Col, Row, Spin } from 'antd';
import { kiraB50Large } from 'assets/images';
import { getBatteryEnergyImage } from 'components/MyMachineDetails/util/battery-images';
import PropType from 'prop-types';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { MAPPING_MACHINE_HEALTH_TEXT } from 'util/config/constant';
import { getMachineType } from 'util/machine-type';
import { TRANSLATION_NAMESPACES } from 'util/translation/constant';
import { formatDate, formatTime } from '../../../../util/date';
import { CircleProgress } from '../../../UI/CircleProgress';
import { fetchMachineDetailsInformation } from '../../state/operation';
import * as selector from '../../state/selector';
import { UpdateMachineField } from '../UpdateMachineField';

const columnLayoutProps = {
  xs: 8,
  sm: 8
};

const columnLayoutHeadingProps = {
  lg: 24,
  xl: 12
};

export const MachineStatus = ({ id }) => {
  const { t } = useTranslation(TRANSLATION_NAMESPACES.MACHINE_DETAILS);
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(fetchMachineDetailsInformation(id));
  }, [dispatch, id]);

  const { machineDetailsInfo } = useSelector((state) => ({
    machineDetailsInfo: selector.selectMachineDetailsInformationInfo(state)
  }));

  const { data, isLoading } = machineDetailsInfo;

  return (
    <div className="machine-status widget">
      <Row className="widget-header">
        <Col {...columnLayoutHeadingProps}>
          <span className="machine-status__title">
            {t('machineStatusPanel.title')}
          </span>
        </Col>
      </Row>

      <Spin spinning={isLoading}>
        <div className="machine-status__content">
          <Row className="machine-status__body">
            <Col
              {...columnLayoutProps}
              className="machine-status__body__cols picture-wrapper">
              <img
                src={data.thumbnail || kiraB50Large}
                alt="Kira B 50"
                className="machine-status__body__cols-img"
              />
            </Col>
            <Col {...columnLayoutProps} className="machine-status__body__cols">
              <div>
                <div className="machine-status__body__cols-center-label">
                  {t('machineStatusPanel.batteryLevel')}
                </div>
                <div className="machine-status__body__cols-center-value">
                  {data.batteryLevel !== null && `${data.batteryLevel ?? 0}%`}
                </div>
              </div>
              <div className="machine-status__body__cols-chart">
                <CircleProgress
                  progress={data.batteryLevel || 0}
                  size={120}
                  strokeWidth={10}>
                  <img
                    src={getBatteryEnergyImage(data.batteryLevel)}
                    alt=""
                    className="battery"
                  />
                </CircleProgress>
              </div>
            </Col>
            <Col {...columnLayoutProps} className="machine-status__body__cols">
              <div>
                <div className="machine-status__body__cols-label">
                  {t('machineStatusPanel.softwareVersion')}
                </div>
                <div className="machine-status__body__cols-value">
                  {data.softwareVersion}
                </div>
              </div>
              <div>
                <div className="machine-status__body__cols-label">
                  {t('machineStatusPanel.machineHealth')}
                </div>
                <div className="machine-status__body__cols-value">
                  {MAPPING_MACHINE_HEALTH_TEXT[data.health]}
                </div>
              </div>
              <div>
                <div className="machine-status__bottom__cols-text bold">
                  {t('machineStatusPanel.lastConnection')}
                </div>
                <div className="machine-status__bottom__cols-text">
                  {formatTime(data.lastConnectedAt)}
                </div>
                <div className="machine-status__bottom__cols-text">
                  {formatDate(data.lastConnectedAt, { shortFormat: true })}
                </div>
              </div>
            </Col>
          </Row>

          <div className="machine-status__bottom">
            <Row className="machine-status__bottom__rows">
              <Col xs={8} sm={8} className="machine-status__bottom__cols">
                <div className="machine-status__bottom__cols-text machine-status__bottom__cols-text--name">
                  {getMachineType(data.materialNumber)}
                </div>
                <div className="machine-status__bottom__cols-text machine-status__bottom__cols-text--alias">
                  <UpdateMachineField
                    id={id}
                    fieldName="alias"
                    value={data.alias}
                  />
                </div>
              </Col>
              <Col
                xs={16}
                sm={16}
                className="machine-status__bottom__cols machine-status__bottom__col-right">
                <div className="machine-status__bottom__cols-heading">
                  {t('machineStatusPanel.lastKnownAddress')}
                </div>
                <div className="machine-status__bottom__cols-value">
                  {data.location?.name}
                </div>
              </Col>
            </Row>
          </div>
        </div>
      </Spin>
    </div>
  );
};

MachineStatus.propTypes = {
  id: PropType.string.isRequired
};
